import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';
import AuthView from 'components/AuthView';
import AuthFeature from 'components/AuthFeature';
import { AUTH_EDIT_FILTER } from 'constants/permission';
import FlexGroup from 'components/FlexGroup';
import SearchField from 'components/SearchField';
import { Translate, I18n } from 'react-redux-i18n';
import { updateDocumentTitle } from 'actions';
import { getDefaultPageSize } from 'components/Pagination/PageSizeSelect';
import MultiselectFilter from 'components/Filter/MultiselectFilter';
import List from './List';
import { getPinnedOptions } from './FilterPinStatus';
import FeatureTypeFilter from './FeatureTypeFilter';
import { useGetFilterListQuery } from 'redux/hook/useFilter';
import FilterImportModal from './FilterImportModal';
import { exportCSV } from 'helpers/util';

import './filter-management.scss';

const EXPORT_FILE_NAME = 'Fleet Filters';
const FILTER_NAME = 'name';
const FILTER_TYPE = 'type';
const FILTER_IS_PINNED = 'pin';

const FilterManagement = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const i18n = useSelector(state => state.i18n);
    const [showImport, setShowImport] = useState(false);
    const [selectedIdList, setSelectedIdList] = useState([]);


    const getQueryString = useCallback((payload) => {
        const { filter, ...params } = payload;
        return {
            ...filter,
            ...params
        };
    },[]);

    const [queryPayload, setQueryPayload] = useState({
        filter: {
            [FILTER_NAME]: undefined,
            [FILTER_TYPE]: undefined,
            [FILTER_IS_PINNED]: undefined,
        },
        sort: undefined,
        size: getDefaultPageSize().value,
        page: 1,
    });
    const [queryString, setQueryString] = useState(getQueryString(queryPayload));

    const {
        data,
        refetch: getFilterList,
    } = useGetFilterListQuery(queryString, {
        pollingInterval: 60000,
        skip: !!(selectedIdList.length > 0),
    });

    const handleFilterChange = type => value => {

        let filter = { ...queryPayload.filter };

        switch (type) {
        case FILTER_NAME:
            const keyword = value === '' ? undefined : value;
            filter = {
                [FILTER_NAME]: keyword,
            };
            break;
        case FILTER_TYPE:
        case FILTER_IS_PINNED:
            filter[type] = [...value.inputSelect];
            break;
        default:
            filter[type] = value;
        }
        setQueryPayload({
            ...queryPayload,
            filter,
            page: 1,
        });
    };

    useEffect(() => {
        const newQueryString = getQueryString(queryPayload);
        setQueryString(newQueryString);
        setSelectedIdList([]);
    },[queryPayload, getQueryString]);

    useEffect(() => {
        const { pathname } = location;
        dispatch(updateDocumentTitle(`routes.${ pathname }`));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {}, [i18n]);

    const fetchFilterList = useCallback(() => {
        return getFilterList(queryString);
    },[getFilterList, queryString]);

    const handleSort = ({ field, order }) => {
        setQueryPayload({
            ...queryPayload,
            page: 1,
            sort: (field && order) ? `${field},${order}` : undefined,
        });

    };

    const handlePageSelect = page => {
        setQueryPayload({
            ...queryPayload,
            page: page,
        });
    };

    const handlePageSizeChange = pageSize => {
        setQueryPayload({
            ...queryPayload,
            page: 1,
            size: pageSize,
        });
    }
    const closeImportModal = e => {
        setShowImport(false);
    };
    const hadleImport = e => {
        setShowImport(true);
    };

    const handleExport = async() => {
        const header = I18n.t('filters.export_title');
        const dataString = [header].concat(selectedIdList);

        exportCSV(EXPORT_FILE_NAME, '', dataString, '\r\n');

    };
    const handleRowSelected = (ids, selectAll) => {
        let updatedScooterList = [...selectedIdList];
        const { data_list } = data || [];
        if (selectAll) {
            updatedScooterList = ids.length === data_list.length ? ids : [];
        }
        else if (ids.length === 0) {
            updatedScooterList = [];
        }
        else {
            ids.forEach(id => {
                if (updatedScooterList.indexOf(id) === -1) {
                    updatedScooterList.push(id);
                }
                else {
                    const idx = updatedScooterList.indexOf(id);
                    updatedScooterList.splice(idx, 1);
                }
            });
        }
        setSelectedIdList(updatedScooterList);
    };
    useEffect(() => {
        const prevSelected = [...selectedIdList];
        const { data_list } = data || {};
        if (data_list?.length && prevSelected.length) {
            const all = Array.from(data_list, ({ id }) => id);
            const allSet = new Set(all);
            prevSelected.forEach((id, index) => {
                if (!allSet.has(id)) {
                    prevSelected.splice(index, 1);

                }
            });
            setSelectedIdList(prevSelected);
        }
        else {
            setSelectedIdList([]);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSelectedIdList, data]);

    return (
        <AuthView className="filter-management">
            <FlexGroup spaceBetween>
                <FlexGroup start className="search-section choice-group-field-container">
                    <FlexGroup start className="choice-group-field">
                        <Translate className="search-for-caption" value="search_for" />
                        <SearchField
                            placeholder={ I18n.t('filters.search_box_placeholder') }
                            onSubmit={ handleFilterChange(FILTER_NAME) }
                            value={ queryPayload.filter[FILTER_NAME] }
                        />
                    </FlexGroup>
                    <FlexGroup start className="filters">
                            <FeatureTypeFilter
                                defaultSelected={ queryPayload.filter[FILTER_TYPE] }
                                onChange={ handleFilterChange(FILTER_TYPE) }
                            />
                            <MultiselectFilter
                                title={ I18n.t('filters.filter_pinned') }
                                options={ getPinnedOptions() }
                                defaultSelected={ queryPayload.filter[FILTER_IS_PINNED] }
                                onChange={ handleFilterChange(FILTER_IS_PINNED) }
                            />
                    </FlexGroup>
                </FlexGroup>
                <AuthFeature requiredList={ [AUTH_EDIT_FILTER] }>
                    <FlexGroup end gap>
                        <Button color="primary" onClick={ hadleImport }>
                            <Translate value="import" />
                        </Button>
                    </FlexGroup>
                </AuthFeature>
            </FlexGroup>
            <List
                onSort={ handleSort }
                onSelect={ handlePageSelect }
                onFetch={ fetchFilterList }
                onChange={ handlePageSizeChange }
                onExport={ handleExport }
                data={ data }
                selectedIdList={ selectedIdList }
                onRowSelected={ handleRowSelected }
            />
            <FilterImportModal
                show={ showImport }
                onClose={ closeImportModal }
            />
        </AuthView>
    );
};

export default FilterManagement;
